import type { ReactElement } from 'react';
import type { DrawnKey, FixedKey } from '@arcnet/contracts';

/**
 * The symbols printed on a desk phone's keys, for the drawn handset (owner, 31 October 2026: "photo
 * realism on the keys like the little mailbox buttons"). Our UI icons are thin outlines made for a
 * page; a phone's keys carry bold, filled marks in white ink. These are drawn that way: solid
 * shapes and heavy strokes on a 24-unit grid, in currentColor, so a key's colour and the theme
 * still decide how they look.
 */

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

/** A phone handset, filled: the call and end keys, and inside hold and transfer. */
const HANDSET = 'M6.6 3.3c.6-.6 1.6-.6 2.2.1l2 2.4c.5.6.5 1.5-.1 2.1L9.3 9.3a11 11 0 0 0 5.4 5.4l1.4-1.4c.6-.6 1.5-.6 2.1-.1l2.4 2c.7.6.7 1.6.1 2.2l-1.4 1.4c-1 1-2.5 1.4-3.9.9A18 18 0 0 1 4.3 8.6c-.5-1.4-.1-2.9.9-3.9z';

const PRINTS: Readonly<Record<string, ReactElement>> = {
  message: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.2" fill="currentColor" />
      <path d="M3.5 6.5 12 13l8.5-6.5" fill="none" stroke="var(--arc-keyprint-cut, #1d2127)" strokeWidth="1.8" strokeLinejoin="round" />
    </>
  ),
  headset: (
    <>
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" {...S} />
      <rect x="2.6" y="12.5" width="4.6" height="7" rx="1.6" fill="currentColor" />
      <rect x="16.8" y="12.5" width="4.6" height="7" rx="1.6" fill="currentColor" />
      <path d="M19 19.5c0 1.6-1.6 2.5-4 2.5h-2" {...S} strokeWidth={1.8} />
    </>
  ),
  mute: (
    <>
      <rect x="8.6" y="2.5" width="6.8" height="11.5" rx="3.4" fill="currentColor" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M8.5 21h7" {...S} strokeWidth={2} />
      <path d="M3.5 3.5l17 17" {...S} stroke="var(--arc-keyprint-cut, #1d2127)" strokeWidth={4.4} />
      <path d="M3.5 3.5l17 17" {...S} strokeWidth={2.2} />
    </>
  ),
  hold: (
    <>
      <rect x="6.2" y="4.5" width="4" height="15" rx="1.2" fill="currentColor" />
      <rect x="13.8" y="4.5" width="4" height="15" rx="1.2" fill="currentColor" />
    </>
  ),
  redial: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.6-5.7" {...S} />
      <path d="M20.6 3.4v5.8h-5.8z" fill="currentColor" />
    </>
  ),
  transfer: (
    <>
      <path d="M3.5 12h12" {...S} />
      <path d="M12.6 6.8 18 12l-5.4 5.2z" fill="currentColor" />
      <path d="M20.5 5v14" {...S} />
    </>
  ),
  speaker: (
    <>
      <path d="M3 9.2h3.6L12 4.5v15l-5.4-4.7H3z" fill="currentColor" />
      <path d="M15.3 8.8a4.6 4.6 0 0 1 0 6.4M18.1 6a8.6 8.6 0 0 1 0 12" {...S} strokeWidth={2} />
    </>
  ),
  cancel: <path d="M6 6l12 12M18 6 6 18" {...S} strokeWidth={2.8} />,
  up: <path d="M12 6.5 19 16H5z" fill="currentColor" />,
  down: <path d="M12 17.5 5 8h14z" fill="currentColor" />,
  left: <path d="M6.5 12 16 5v14z" fill="currentColor" />,
  right: <path d="M17.5 12 8 19V5z" fill="currentColor" />,
  conference: (
    <>
      <circle cx="12" cy="7" r="3.2" fill="currentColor" />
      <path d="M6.5 19a5.5 5.5 0 0 1 11 0z" fill="currentColor" />
      <circle cx="4.6" cy="9.5" r="2.2" fill="currentColor" />
      <circle cx="19.4" cy="9.5" r="2.2" fill="currentColor" />
      <path d="M1 18a3.6 3.6 0 0 1 5-3.3M23 18a3.6 3.6 0 0 0-5-3.3" {...S} strokeWidth={1.8} />
    </>
  ),
  directory: (
    <>
      <path d="M6 2.8h11.5a1.5 1.5 0 0 1 1.5 1.5v15.4a1.5 1.5 0 0 1-1.5 1.5H6a2 2 0 0 1-2-2V4.8a2 2 0 0 1 2-2z" fill="currentColor" />
      <circle cx="11.6" cy="9.6" r="2.4" fill="var(--arc-keyprint-cut, #1d2127)" />
      <path d="M7.8 16.4a3.9 3.9 0 0 1 7.6 0z" fill="var(--arc-keyprint-cut, #1d2127)" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="8.6" {...S} />
      <path d="M12 7.2V12l3.4 2.2" {...S} strokeWidth={2.2} />
    </>
  ),
  call: <path d={HANDSET} fill="currentColor" />,
  end: <path d={HANDSET} fill="currentColor" transform="rotate(135 12 12)" />,
  // Marks a model prints in place of the usual ones (HandsetDrawing.prints): the W73H's ring and keypad.
  calllog: (
    <>
      <path d="M5 19 13.5 10.5M5 19v-5.2M5 19h5.2" {...S} strokeWidth={2.2} />
      <path d="M19 5l-8.5 8.5M19 5v5.2M19 5h-5.2" {...S} strokeWidth={2.2} />
    </>
  ),
  book: (
    <>
      <path d="M12 6.5C9.6 4.9 6.6 4.6 3.5 5.2v13c3.1-.6 6.1-.3 8.5 1.3 2.4-1.6 5.4-1.9 8.5-1.3v-13c-3.1-.6-6.1-.3-8.5 1.3z" fill="currentColor" />
      <path d="M12 6.8v12.4" {...S} stroke="var(--arc-keyprint-cut, #1d2127)" strokeWidth={1.6} />
    </>
  ),
  minus: <path d="M5 12h14" {...S} strokeWidth={2.8} />,
  circle: <circle cx="12" cy="12" r="6" {...S} strokeWidth={2} />,
  plus: <path d="M5 12h14M12 5v14" {...S} strokeWidth={2.8} />,
  voicemail: (
    <>
      <circle cx="6.5" cy="13" r="3.6" {...S} strokeWidth={2.2} />
      <circle cx="17.5" cy="13" r="3.6" {...S} strokeWidth={2.2} />
      <path d="M6.5 16.6h11" {...S} strokeWidth={2.2} />
    </>
  ),
  lock: (
    <>
      <path d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5" {...S} strokeWidth={2.2} />
      <rect x="5.2" y="10.3" width="13.6" height="10.2" rx="1.8" fill="currentColor" />
    </>
  ),
  // The T7x/T8x's transfer key: a handset in two halves, passing the call on (Yealink's photos).
  handoff: (
    <>
      <path d="M6.5 4.5c-2.6 4.3-2.6 10.7 0 15M17.5 4.5c2.6 4.3 2.6 10.7 0 15" {...S} strokeWidth={2.6} />
      <path d="M9 12h5.5" {...S} strokeWidth={2.2} />
      <path d="M12.2 9 15.5 12l-3.3 3z" fill="currentColor" />
    </>
  ),
  // The T7x/T8x's page key: a pair of chevrons in brackets (Yealink's T73/T74/T85 photos).
  page: (
    <>
      <path d="M5.5 5.5H2.5v13h3M18.5 5.5h3v13h-3" {...S} strokeWidth={1.8} />
      <path d="M9.5 8.5 6 12l3.5 3.5M14.5 8.5 18 12l-3.5 3.5" {...S} strokeWidth={2.2} />
    </>
  ),
};

/**
 * The printed symbol for a key, or null for one with none (OK, the soft keys). `print` names a
 * different mark where the model has one (HandsetDrawing.prints): a print's name, 'none' for a plain
 * key, or 'text:TRAN' for letters printed on it.
 */
export function KeyPrint({ k, print: named }: { k: FixedKey | DrawnKey | string; print?: string | undefined }) {
  const name = named ?? k;
  if (name === 'none') return null;
  if (name.startsWith('text:')) return <span className="arc-keyprint-text">{name.slice(5)}</span>;
  const print = PRINTS[name];
  if (!print) return null;
  return (
    <svg className="arc-keyprint" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false">
      {print}
    </svg>
  );
}
